<template>
<Layout>
  <div class="login-container">
    <div class="logo" v-if="!this.el.is.tabletUp">
      <img :src="clientLogo" />
    </div>
    <form class="form" @submit.prevent="handleLogin">
      <h1>Sign-In</h1>
      <BaseInput :class="[{ 'email-error': this.emailError || this.passError }]" v-model="username" name="username" placeholder="E-mail Address" />
      <p class="errorMsg" v-if="this.emailError">{{ this.emailError }}</p>
      <BaseInput :class="[{ 'pass-error': this.passError }]" v-model="password" name="password" type="password" placeholder="Password" />
      <p class="errorMsg" v-if="this.passError">{{ this.passError }}</p>

      <BaseButton :disabled="authenticating" type="submit" class="loginButton">
        <BaseIcon v-if="authenticating" name="sync" spin />
        <span v-else>Log in</span>
      </BaseButton>

      <div class="signUp">
        <p>New here?</p>
        <router-link :to="{ name: 'signup' }" class="onboarding-link">Sign Up</router-link>
      </div>
      <div class="forgotPasswordWrapper">
        <div class="forgotPassword">
          <router-link class="onboarding-link" :to="{ name: 'resetPassword' }">Forgot Password?</router-link>
        </div>
      </div>
    </form>
  </div>
</Layout>
</template>

<script>
import Layout from 'layouts/embed';
import { breakpointsMixin } from 'breakpointsMixin';
import { gtmMixin } from 'gtmMixin';
import { authApiMethods, mainMethods, authComputed, userMethods } from '@state/helpers';
import appConfig from '@src/app.config.ES6';

export default {
  page: {
    title: 'Log in',
    meta: [{ name: 'description', content: `Log in to ${appConfig.title}` }],
  },
  props: {
    route: {
      type: Object,
    },
  },
  mixins: [breakpointsMixin, gtmMixin],
  components: { Layout },
  data() {
    return {
      username: '',
      password: '',
      emailError: null,
      passError: null,
      authenticating: false,
    };
  },
  computed: {
    clientName() {
      return appConfig.NAME ? appConfig.NAME : null;
    },
    clientLogo() {
      return appConfig.LOGO;
    },
    placeholders() {
      return appConfig.NODE_ENV === 'production'
        ? {}
        : {
            username: 'E-mail Address',
            password: 'Password',
          };
    },
  },
  beforeMount() {
    const loadingTxt = this.$route.params.fromLogout ? 'Logging you out.' : 'Signing you in.';
    this.showLoader(loadingTxt);
  },
  mounted() {
    this.gtmTrackEvent('pageView', { action: 'Visit Login', label: 'Login View' });
    setTimeout(() => this.hideLoader(), 2000);
  },
  methods: {
    ...authApiMethods,
    ...mainMethods,
    ...userMethods,
    handleAuthErrors(err) {
      this.authenticating = false;
      switch (err.status) {
        case 401:
          this.emailError = '';
          this.passError = `E-mail or password is incorrect`;
          break;
      }
    },
    formValid() {
      if (this.username && this.password) {
        return true;
      } else {
        if (!this.password || this.password === '') {
          this.passError = `Password can't be blank`;
        } else if (this.password.length < 8) {
          this.passError = `Password must be at least 8 characters`;
        }

        if (!this.password || this.password === '') {
          this.emailError = `Email can't be blank`;
        } else if (!this.email.includes('@')) {
          this.emailError = `A valid email address is required`;
        }
        return false;
      }
    },
    async getUser() {
      await this.getUserProfile();
      await this.$router.push(this.$route.query.redirectFrom || { name: 'home' });
      this.authenticating = false;
    },
    handleLogin() {
      if (this.formValid()) {
        this.authenticating = true;

        return this.logIn({
          username: this.username,
          password: this.password,
        })
          .then((token) => this.getUser())
          .catch((err) => this.handleAuthErrors(err));
      }
    },
  },
  watch: {
    username(val) {
      if (this.emailError) {
        this.emailError = null;
      }
    },
    password(val) {
      if (this.passError) {
        this.passError = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'theme.scss';

.onboarding-link {
  color: $color-button-text;
  text-decoration: underline;
}

.login-container {
  align-items: center;
  background-color: $color-action;
  bottom: 0;
  display: flex;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
}

.logo {
  display: flex;
  justify-content: center;
  position: absolute;
  top: 3.5rem;
  width: 100%;

  img {
    height: auto;
    max-height: 15rem;
    width: auto;
  }
}

.form {
  margin: 4rem auto 2rem;
  text-align: center;
  width: 75%;

  // iPad up
  @include media('xl', null) {
    width: 50%;
  }

  // iPad Pro up
  @include media('xxl', null) {
    width: 38%;
  }

  h1 {
    color: $color-button-text;
    font-size: 4rem;
    margin-bottom: 3.2rem;
  }

  .forgotPasswordWrapper {
    bottom: 3rem;
    left: 0;
    position: absolute;
    right: 0;
  }

  .signUp {
    align-items: center;
    display: flex;
    flex-direction: column;
    font-size: 1.6rem;
    margin-top: 2rem;
  }

  input {
    line-height: 2;
    margin: 0 auto 1rem auto;
    max-width: 37.5rem;

    &::placeholder {
      color: $color-text;
      line-height: 2;
      opacity: 0.5;
    }

    &.email-error,
    &.pass-error {
      border-color: $color-error;
      border-width: 0.2rem;
      margin-bottom: 1rem;
    }
  }

  .errorMsg {
    color: $color-error;
    font-size: 1.4rem;
    font-weight: 400;
    margin: 0 auto 2rem auto;
    max-width: 37.5rem;
    text-align: left;
  }

  .loginButton {
    background-color: $color-button-text;
    color: $color-action;
    padding: 1rem 2rem;
  }

  .forgotPassword {
    font-size: 1.7rem;
    text-decoration: underline;
  }
}
</style>
